import { isBrowser } from "../../utils/environment";
import { db } from "../config";
import Cookies from "js-cookie";
import {
  increment,
  doc,
  getDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { getAnalytics, logEvent } from "firebase/analytics";

let analytics = null;

// Helper to check if analytics are allowed
const isAnalyticsAllowed = () => {
  const consent = Cookies.get("cookie-consent");
  return consent === "accepted";
};

// Initialize analytics only if consent is given and in browser environment
if (isBrowser && isAnalyticsAllowed()) {
  analytics = getAnalytics();
}

// Helper function to safely log events
const logAnalyticsEvent = (eventName, eventParams = {}) => {
  if (analytics && isAnalyticsAllowed()) {
    logEvent(analytics, eventName, eventParams);
  }
};

// Helper for essential analytics that should always be tracked
const logEssentialEvent = async (collection, docId, data) => {
  try {
    // These events are considered essential for site functionality
    // and are stored in Firestore regardless of cookie consent
    const docRef = doc(db, collection, docId);
    await setDoc(docRef, data, { merge: true });
  } catch (error) {
    console.error(`Error logging essential event to ${collection}:`, error);
  }
};

// Reading Progress Events
export const trackReadingProgress = (userId, bookId, progressData) => {
  if (!isAnalyticsAllowed()) return;

  logAnalyticsEvent("update_reading_progress", {
    user_id: userId,
    book_id: bookId,
    current_page: progressData.currentPage,
    time_spent: progressData.timeSpent,
    read_date: progressData.readDate,
  });
};

export const trackBookCompletion = (userId, bookId, totalPages) => {
  logAnalyticsEvent("complete_book", {
    user_id: userId,
    book_id: bookId,
    total_pages: totalPages,
  });
};

// Social Interaction Events
export const trackFollowUser = (followerId, followedId) => {
  logAnalyticsEvent("follow_user", {
    follower_id: followerId,
    followed_id: followedId,
  });
};

export const trackUnfollowUser = (followerId, followedId) => {
  logAnalyticsEvent("unfollow_user", {
    follower_id: followerId,
    followed_id: followedId,
  });
};

export const trackCreateList = (userId, listName) => {
  logAnalyticsEvent("create_reading_list", {
    user_id: userId,
    list_name: listName,
  });
};

export const trackFollowList = (userId, listId, listName) => {
  logAnalyticsEvent("follow_reading_list", {
    user_id: userId,
    list_id: listId,
    list_name: listName,
  });
};

export const trackBookRating = (userId, bookId, rating) => {
  logAnalyticsEvent("rate_book", {
    user_id: userId,
    book_id: bookId,
    rating: rating,
  });
};

export const trackReviewInteraction = (type, userId, reviewId) => {
  logAnalyticsEvent("review_interaction", {
    interaction_type: type, // 'create', 'like', 'comment'
    user_id: userId,
    review_id: reviewId,
  });
};

// User Account Events
export const trackSignUp = (userId, signUpMethod) => {
  logAnalyticsEvent("sign_up", {
    user_id: userId,
    method: signUpMethod,
  });
};

export const trackProfileUpdate = (userId, updatedFields) => {
  logAnalyticsEvent("profile_update", {
    user_id: userId,
    updated_fields: Object.keys(updatedFields),
  });
};

export const trackSearch = (userId, searchType, searchTerm) => {
  logAnalyticsEvent("search", {
    user_id: userId,
    type: searchType, // 'users', 'books', 'lists'
    term: searchTerm,
  });
};

// Book Interaction Events
export const trackBookView = async (
  bookId,
  userId = null,
  bookTitle = null
) => {
  try {
    // Firebase Analytics (non-essential)
    if (isAnalyticsAllowed() && userId) {
      logAnalyticsEvent("view_book", {
        user_id: userId,
        book_id: bookId,
        book_title: bookTitle,
      });
    }

    // Essential tracking for book recommendations (always tracked)
    const today = new Date().toISOString().split("T")[0];
    await logEssentialEvent("Analytics/bookViews/daily", today, {
      [`views.${bookId}`]: increment(1),
      createdAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error tracking book view:", error);
  }
};

export const trackReadingStatusChange = (userId, bookId, newStatus) => {
  logAnalyticsEvent("change_reading_status", {
    user_id: userId,
    book_id: bookId,
    new_status: newStatus,
  });
};

// Track search terms
export const trackSearchTerm = async (searchTerm) => {
  try {
    const normalizedTerm = searchTerm.toLowerCase().trim();
    if (!normalizedTerm) return;

    // Essential tracking for search functionality (always tracked)
    const today = new Date().toISOString().split("T")[0];
    await logEssentialEvent("Analytics/searchTerms/daily", today, {
      [`terms.${normalizedTerm}`]: increment(1),
      createdAt: Timestamp.now(),
    });

    // Non-essential analytics tracking
    if (isAnalyticsAllowed()) {
      logAnalyticsEvent("search", {
        term: normalizedTerm,
      });
    }
  } catch (error) {
    console.error("Error tracking search term:", error);
  }
};

// Get most viewed books
export const getMostViewedBooks = async (days = 7) => {
  try {
    // Generate an array of date strings for the past 'days' days
    const dateStrings = [];
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      dateStrings.push(date.toISOString().split("T")[0]);
    }

    const analyticsRef = doc(db, "Analytics", "bookViews");
    const viewCounts = {};

    // Fetch each day's data individually
    await Promise.all(
      dateStrings.map(async (dateString) => {
        const dailyDocRef = doc(analyticsRef, "daily", dateString);
        const dailyDoc = await getDoc(dailyDocRef);

        if (dailyDoc.exists()) {
          const views = dailyDoc.data().views || {};
          Object.entries(views).forEach(([bookId, count]) => {
            viewCounts[bookId] = (viewCounts[bookId] || 0) + count;
          });
        }
      })
    );

    // Convert to array and sort
    const sortedBooks = Object.entries(viewCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);

    // Get book details
    return Promise.all(
      sortedBooks.map(async ([bookId, views]) => {
        const bookDoc = await getDoc(doc(db, "Books", bookId));
        const bookData = bookDoc.data();
        return {
          id: bookId,
          title: bookData?.title || "Unknown Book",
          coverUrl: bookData?.coverUrl || "/images/placeholder-book.jpg",
          views,
        };
      })
    );
  } catch (error) {
    console.error("Error getting most viewed books:", error);
    return [];
  }
};

// Get popular search terms
export const getPopularSearchTerms = async (days = 7) => {
  try {
    // Generate an array of date strings for the past 'days' days
    const dateStrings = [];
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      dateStrings.push(date.toISOString().split("T")[0]);
    }

    const analyticsRef = doc(db, "Analytics", "searchTerms");
    const termCounts = {};

    // Fetch each day's data individually
    await Promise.all(
      dateStrings.map(async (dateString) => {
        const dailyDocRef = doc(analyticsRef, "daily", dateString);
        const dailyDoc = await getDoc(dailyDocRef);

        if (dailyDoc.exists()) {
          const terms = dailyDoc.data().terms || {};
          Object.entries(terms).forEach(([term, count]) => {
            termCounts[term] = (termCounts[term] || 0) + count;
          });
        }
      })
    );

    // Convert to array, sort, and return top 10
    return Object.entries(termCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([term, count]) => ({
        term,
        count,
      }));
  } catch (error) {
    console.error("Error getting popular search terms:", error);
    return [];
  }
};
